<template>
  <div class="card mb-4 min-w-0 overflow-x-hidden">
    <div class="flex flex-col max-h-min p-2 sm:p-4">
      <div
        :role="canExpandAnswers ? 'button' : undefined"
        :tabindex="canExpandAnswers ? 0 : undefined"
        :class="{ 'cursor-pointer': canExpandAnswers }"
        class="flex flex-row flex-wrap items-stretch sm:items-center justify-between gap-2 sm:gap-4 min-w-0"
        @click="canExpandAnswers && toggleQuestionContainer()"
        @keydown.enter.prevent="canExpandAnswers && toggleQuestionContainer()"
        @keydown.space.prevent="canExpandAnswers && toggleQuestionContainer()"
      >
        <div class="flex flex-col flex-1 min-w-0">
          <p id="title" class="text-base sm:text-lg font-medium break-words min-w-0">
            {{ workshop.workshopTitle }}
          </p>
          <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 sm:mt-2 text-sm sm:text-base">
            <p class="text-[var(--color-orange)] flex items-center gap-1">
              <font-awesome-icon icon="fa-calendar" class="shrink-0" />
              {{ formattedDate }}
            </p>
            <p class="text-[var(--color-disabled)]">{{ workshop.workshopFacilitator?.name }}</p>
          </div>
        </div>
        <div class="flex flex-col items-end gap-2 sm:flex-row sm:items-center sm:gap-4 shrink-0">
          <font-awesome-icon
            v-if="workshop.permissionToAction"
            @click.stop="toggleEditWorkshop"
            class="cursor-pointer text-[var(--color-disabled)] hover:text-[var(--color-orange)] shrink-0 order-1 sm:order-none"
            icon="fa-pen"
          />
          <div class="flex flex-row flex-wrap items-center justify-end gap-2 sm:gap-4 order-2 sm:order-none w-full sm:w-auto">
            <Button
              v-if="workshop.permissionToAction"
              type="button"
              :class="isSentReflection ? 'btn-primary-accent' : 'btn-secondary opacity-60 cursor-not-allowed'"
              :disabled="!isSentReflection"
              @click.stop="toggleShowReflectionParticipants" unstyled>
              {{ $t('WorkshopComponent.SendReflection') }}
            </Button>
            <font-awesome-icon
              v-if="canExpandAnswers"
              :class="{ 'rotate-180': showQuestions }"
              class="shrink-0 text-[var(--color-disabled)] hover:text-[var(--color-orange)] transition-colors"
              icon="fa-chevron-down"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
      <div :class="{ 'flex flex-col': showQuestions, 'hidden': !showQuestions }" class="pt-4 pr-0 sm:pr-2">
        <template v-if="questions.length > 0">
          <workshop-question-component
            v-for="(question, index) in questions"
            :key="index"
            :question="question"
          />
        </template>
        <template v-else>
          <p class="text-center text-[var(--color-disabled)] text-sm sm:text-base">
            {{ $t('WorkshopComponent.dataUpdating') }}
          </p>
        </template>
      </div>
    </div>
    <workshop-form-component :value="workshopEditData" v-if="showEdit"
      @hide-details="toggleEditWorkshop"/>
    <WorkshopParticipantsOverlayComponent :visible="showDialog"
      @update:visible="showDialog = $event" />
  </div>
</template>
<script>
import WorkshopQuestionComponent from '@/components/Home/WorkshopComponent/QuestionComponent.vue';
import WorkshopFormComponent from './WorkshopFormComponent.vue';
import NetworkWorkshop from '@/helpers/networking/NetworkWorkshop';
import Button from 'primevue/button';
import WorkshopParticipantsOverlayComponent from './WorkshopParticipantsOverlayComponent.vue';

export default {
  name: 'WorkshopResponseComponent',
  props: {
    workshop: Object,
  },
  components: {
    WorkshopQuestionComponent,
    WorkshopFormComponent,
    Button,
    WorkshopParticipantsOverlayComponent,
  },
  data() {
    return {
      showQuestions: false,
      questions: [],
      questionCount: [1, 2, 3, 4],
      showParticipants: false,
      showPopup: false,
      showEdit: false,
      showDialog: false,
    };
  },
  methods: {
    toggleEditWorkshop() {
      this.showEdit = !this.showEdit;
      this.showQuestions = false;
    },
    
    async toggleQuestionContainer() {
      // FA-09.19: only facilitator / authorized roles may open aggregated answers
      if (!this.canExpandAnswers) {
        return;
      }
      this.showQuestions = !this.showQuestions;
      this.showEdit = false;
      if (this.showQuestions === true) {
        try {
          const { data } = await NetworkWorkshop.getQuestionAnswers({
            seriesId: this.workshop._id,
          });
          this.questions = data.questions;
        } catch (error) {
          this.questions = [];
        }
      }
    },
    async toggleShowReflectionParticipants() {
      this.showParticipants = !this.showParticipants;
      try {
        await this.$store.commit('setSelectedWorkshop', this.workshop);
        /* this.$store.commit('addFrontendOverlay', {
          id: Math.random(),
          type: 'workshopParticipants',
        }); */
        this.showDialog = true;
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: '',
          life: 3000,
        });
      }
    },
  },
  computed: {
    workshopEditData() {
      return {
        ...this.workshop,
        dateWorkshopBegin: this.workshop.dateWorkshopBegin
          ? new Date(this.workshop.dateWorkshopBegin)
          : new Date(),
        dateWorkshopEnd: this.workshop.dateWorkshopEnd
          ? new Date(this.workshop.dateWorkshopEnd)
          : new Date(),
      };
    },
    formattedDate() {
      const date = new Date(this.workshop.createdAt);
      return `${`0${date.getDate()}`.slice(-2)}.${`0${date.getMonth() + 1}`.slice(
        -2,
      )}.${date.getFullYear()}`;
    },
    isSentReflection() {
      // Misnamed legacy flag: true while send-reflection is still available (not yet sent)
      return !this.workshop.sentReflexion;
    },
    /**
     * FA-09.18 / FA-09.19: expand aggregated answers only for facilitator/creator/superuser
     * after reflection was sent. Pure participants never expand (nothing for them to see).
     */
    canExpandAnswers() {
      return Boolean(this.workshop?.permissionToAction) && Boolean(this.workshop?.sentReflexion);
    },
  },
};
</script>
